import { LEGAL } from "@/lib/legal/config";

/**
 * 개인정보 보호법 제30조(처리방침 필수 기재사항) 기준으로 점검했다(2026-09-26).
 * 변호사 검토를 거친 문서가 아니다 — 공개 전에 전문가 검토를 받을 것.
 */
export default function PrivacyPage() {
  return (
    <>
      <div>
        <h1>개인정보 처리방침</h1>
        <p className="mt-2 text-muted-foreground">시행일: {LEGAL.version}</p>
      </div>

      <p>
        {LEGAL.operatorName}(이하 &quot;운영자&quot;)은 {LEGAL.serviceName} 서비스(이하 &quot;서비스&quot;)를
        제공하면서 이용자의 개인정보를 아래와 같이 처리합니다.
      </p>

      <section id="1" className="flex flex-col gap-2">
        <h2>1. 수집하는 항목과 목적</h2>
        <table>
          <thead>
            <tr>
              <th>항목</th>
              <th>목적</th>
              <th>구분</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>이메일, 비밀번호(암호화되어 저장)</td>
              <td>회원 가입·로그인, 본인 확인</td>
              <td>필수</td>
            </tr>
            <tr>
              <td>설교 노트 사진, 사진에서 읽어낸 글, AI가 만든 요약·마인드맵·실천 후보, 이용자가 정한 실천 항목과 진행 상태, 검색 질문</td>
              <td>노트 정리·보관·검색 기능 제공</td>
              <td>필수(서비스 이용 시 생성)</td>
            </tr>
            <tr>
              <td>
                접속 오류 기록(오류 내용, 기기·브라우저 정보), 오류 발생 시 화면 재현 기록(텍스트·입력값은 자동으로
                가려진 상태로 저장)
              </td>
              <td>서비스 오류 파악과 개선</td>
              <td>자동 수집</td>
            </tr>
          </tbody>
        </table>
        <p>
          설교 노트에는 신앙 활동을 드러내는 내용이 담길 수 있어 <strong>민감정보(종교·신념 관련 정보)</strong>에
          해당할 수 있습니다. 이 정보는 가입 시 다른 동의와 구분하여 별도의 동의를 받아 처리하며, 위 목적 외에는
          사용하지 않습니다.
        </p>
        <p>
          이용자는 개인정보 수집·이용과 민감정보 처리에 동의하지 않을 권리가 있습니다. 다만 필수 항목에 동의하지
          않으면 가입과 서비스 이용이 제한됩니다.
        </p>
        <p>
          남용을 막기 위해 노트 사진은 JPG, PNG, WEBP, HEIC 형식과 4MB 이하로 제한하고, 무료 플랜은 한 달에 노트
          3개까지만 새로 만들 수 있습니다. 이 한도는 서비스 운영 방식이 바뀌면(예: 유료 플랜 도입) 달라질 수
          있으며, 그때는 이 페이지에 갱신해 안내합니다.
        </p>
      </section>

      <section id="2" className="flex flex-col gap-2">
        <h2>2. 보유·이용 기간과 파기</h2>
        <ul>
          <li>회원 탈퇴 시 계정과 모든 노트(사진 포함), 실천 기록, 검색 기록을 즉시 삭제합니다.</li>
          <li>이용자가 개별 노트를 삭제하면 해당 노트의 사진과 이를 근거로 한 검색 답변 기록도 함께 삭제됩니다.</li>
          <li>
            탈퇴하거나 노트를 삭제해도, <strong>그 전에 Google(Gemini)·OpenAI로 이미 전송되어 처리된 데이터는
            운영자가 대신 지울 수 없습니다.</strong> 두 회사는 각자의 정책에 따라 별도로 데이터를 보관·파기합니다.
          </li>
          <li>
            파기 방법: 데이터베이스의 정보는 삭제 명령으로 지우고, 사진 파일은 저장소에서 삭제합니다.
            데이터베이스 백업이 있는 경우 백업본은 정해진 보관 기간이 지나면 자동으로 삭제됩니다.
          </li>
          <li>
            법령에 따라 보관해야 하는 정보는 현재 수집하지 않습니다. 오류 기록은 Sentry 요금제 기준 최대 90일간
            보관 후 자동 삭제됩니다.
          </li>
        </ul>
      </section>

      <section id="3" className="flex flex-col gap-2">
        <h2>3. 처리 위탁 및 국외 이전</h2>
        <p>서비스 제공을 위해 아래 업체에 개인정보 처리를 맡기고 있으며, 일부는 국외 서버에서 처리됩니다.</p>
        <table>
          <thead>
            <tr>
              <th>수탁자</th>
              <th>어떤 업체인가요</th>
              <th>맡기는 업무</th>
              <th>이전되는 정보</th>
              <th>이전 국가 / 시기·방법</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Google LLC (Gemini API)</td>
              <td>미국의 IT 기업입니다. Gemini는 이 회사가 만든 AI 모델로, 사진 속 글을 읽고 요약을 만드는 데 씁니다.</td>
              <td>노트 사진의 글 읽기, 요약·마인드맵·실천 후보 생성</td>
              <td>노트 사진, 이를 분석하는 데 필요한 정보</td>
              <td>미국(글로벌 처리, 지정 리전 없음) / 노트를 올릴 때 네트워크 전송</td>
            </tr>
            <tr>
              <td>OpenAI, L.L.C. (임베딩 API)</td>
              <td>미국의 AI 기업으로 ChatGPT를 만든 곳입니다. 글의 의미를 숫자로 바꿔 비슷한 노트를 찾는 데 씁니다.</td>
              <td>검색을 위한 텍스트 벡터 변환</td>
              <td>노트에서 읽어낸 글, 검색 질문</td>
              <td>미국 / 노트를 올리거나 검색할 때 전송</td>
            </tr>
            <tr>
              <td>Supabase Inc.</td>
              <td>미국의 데이터베이스·로그인 서비스 업체입니다. 계정과 노트를 저장하는 곳입니다.</td>
              <td>계정 인증, 데이터베이스와 사진 저장</td>
              <td>계정 정보와 모든 노트 데이터</td>
              <td>대한민국(서울) / 상시</td>
            </tr>
            <tr>
              <td>Vercel Inc.</td>
              <td>미국의 웹 호스팅 업체입니다. 이 웹사이트를 인터넷에 올려 두는 곳입니다.</td>
              <td>웹 서비스 호스팅(서버리스 함수는 대한민국 서울 리전에서 실행)</td>
              <td>서비스 이용 중 오가는 모든 데이터</td>
              <td>미국(회사 소재지) 및 대한민국(함수 실행 리전) / 상시</td>
            </tr>
            <tr>
              <td>Functional Software, Inc. (Sentry)</td>
              <td>미국의 오류 모니터링 서비스 업체입니다. 서비스에 생긴 오류를 알아내는 데 씁니다.</td>
              <td>오류 모니터링</td>
              <td>오류 기록</td>
              <td>미국 / 오류 발생 시</td>
            </tr>
            <tr>
              <td>Cloudflare, Inc.</td>
              <td>
                미국의 네트워크·보안 업체입니다. 도메인 관리와 함께, 쿠키 없는 방문 통계(Cloudflare Web
                Analytics)에도 씁니다.
              </td>
              <td>방문 통계(집계), 도메인 연결</td>
              <td>접속 시 자동 생성되는 정보(IP 주소, 접속 국가, 방문 페이지 등 집계용)</td>
              <td>미국 / 접속 시</td>
            </tr>
          </tbody>
        </table>
        <p>
          Gemini API와 OpenAI API 모두 <strong>유료 사용량에 대해서는 이용자의 입력을 자체 AI 모델 학습에 쓰지
          않습니다</strong>(무료 사용량에는 이 보장이 적용되지 않을 수 있어, 운영 환경은 유료 사용량으로 전환해
          운영합니다). 그 밖의 목적(부정 사용 탐지 등)으로 각 사가 일정 기간 데이터를 보관할 수 있으며, 자세한
          내용은 각 업체의 개인정보처리방침에서 확인할 수 있습니다. 국외 이전에 동의하지 않을 권리가 있으며, 동의하지
          않으시면 노트 분석·검색 등 서비스의 핵심 기능을 이용할 수 없어 가입이 제한됩니다.
        </p>
        <p>각 업체의 개인정보 처리 방침과 문의 방법은 아래에서 확인할 수 있습니다.</p>
        <ul>
          <li>
            Google:{" "}
            <a className="underline underline-offset-4" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
              policies.google.com/privacy
            </a>
          </li>
          <li>
            OpenAI:{" "}
            <a className="underline underline-offset-4" href="https://openai.com/policies/privacy-policy" target="_blank" rel="noreferrer">
              openai.com/policies/privacy-policy
            </a>
          </li>
          <li>
            Supabase:{" "}
            <a className="underline underline-offset-4" href="https://supabase.com/privacy" target="_blank" rel="noreferrer">
              supabase.com/privacy
            </a>
          </li>
          <li>
            Vercel:{" "}
            <a className="underline underline-offset-4" href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noreferrer">
              vercel.com/legal/privacy-policy
            </a>
          </li>
          <li>
            Sentry:{" "}
            <a className="underline underline-offset-4" href="https://sentry.io/privacy/" target="_blank" rel="noreferrer">
              sentry.io/privacy
            </a>
          </li>
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h2>4. 제3자 제공</h2>
        <p>운영자는 이용자의 개인정보를 법령에 근거가 있는 경우를 제외하고 제3자에게 제공하지 않습니다.</p>
      </section>

      <section className="flex flex-col gap-2">
        <h2>5. 이용자의 권리</h2>
        <ul>
          <li>이용자는 언제든지 자신의 개인정보를 열람하고, 노트를 삭제하며, 설정 화면에서 회원 탈퇴를 할 수 있습니다.</li>
          <li>
            그 밖의 열람·정정·삭제·처리정지 요구는 아래 &quot;개인정보 보호책임자와 문의&quot;의 이메일로
            접수하면 10일 이내에 처리하고 결과를 알려드립니다. 처리가 지연되는 경우 그 사유와 처리 예정일을
            미리 안내합니다.
          </li>
          <li>본인 확인이 필요한 경우 가입한 이메일 주소로 확인을 요청할 수 있습니다.</li>
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h2>6. AI를 이용한 자동 처리</h2>
        <p>
          서비스는 이용자가 올린 사진을 AI(Google Gemini)로 분석해 글을 읽고 요약·마인드맵·실천 후보를 만들며,
          검색을 위해 글을 숫자 값(임베딩)으로 바꿉니다. 이 결과는 이용자 본인에게 보여주는 참고용이며, 이용자의
          권리·의무에 중대한 영향을 미치는 결정을 AI가 자동으로 내리지 않습니다.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2>7. 쿠키와 브라우저 저장소</h2>
        <p>
          로그인 상태를 유지하기 위한 필수 쿠키를 사용하며, 광고·추적 목적의 쿠키는 사용하지 않습니다. 오류
          모니터링(Sentry)이 화면 재현 기록을 위해 임의의 세션 식별자를 브라우저 저장소에 둘 수 있습니다.
        </p>
        <p>
          방문 통계 파악을 위해 Cloudflare Web Analytics를 사용합니다. 이 도구는 쿠키를 사용하지 않고, 개별
          방문자를 식별하지 않으며, 방문 수·접속 국가·유입 경로 같은 집계 정보만 수집합니다. 다른 분석·광고
          도구를 추가로 도입하게 되면 이 방침을 먼저 고치고 안내합니다.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2>8. 안전성 확보 조치</h2>
        <ul>
          <li>사진은 비공개 저장소에 두고, 각 이용자는 본인의 데이터에만 접근할 수 있도록 데이터베이스 접근 규칙을 적용합니다.</li>
          <li>비밀번호는 암호화하여 저장하며 운영자도 원문을 알 수 없습니다.</li>
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h2>9. 만 14세 미만</h2>
        <p>
          서비스는 만 14세 이상만 가입할 수 있고, 가입 시 본인이 만 14세 이상임을 직접 확인받습니다. 만 14세
          미만이 가입한 사실을 알게 되면 법정대리인의 동의를 받은 경우가 아닌 한 지체 없이 그 계정과 정보를
          삭제합니다.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2>10. 개인정보 유출 등 통지</h2>
        <p>
          개인정보가 유출되었음을 알게 되면 지체 없이 유출된 항목, 발생 시점, 이용자가 취할 수 있는 조치, 운영자의
          대응 조치, 문의처를 이용자에게 알립니다. 피해 확산을 막기 위한 조치를 함께 취하며, 법령이 정한 규모
          이상의 유출이면 관계 기관에도 신고합니다.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2>11. 개인정보 보호책임자와 문의, 권익침해 구제방법</h2>
        <ul>
          <li>책임자: {LEGAL.operatorName}</li>
          <li>이메일: {LEGAL.contactEmail}</li>
        </ul>
        <p>개인정보 침해로 인한 피해 구제나 상담이 필요하면 아래 기관에 문의할 수 있습니다.</p>
        <ul>
          <li>개인정보침해신고센터(한국인터넷진흥원): privacy.kisa.or.kr, 국번 없이 118</li>
          <li>개인정보 분쟁조정위원회: 개인정보 포털(privacy.go.kr)에서 분쟁조정 신청</li>
          <li>대검찰청 사이버수사과, 경찰청 사이버수사국: 각 기관 누리집에서 안내</li>
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h2>12. 변경</h2>
        <p>내용이 바뀌면 시행일과 함께 이 페이지에 공지하고, 중요한 변경은 재동의를 받습니다.</p>
      </section>
    </>
  );
}
